import type { Call, INotification } from '@telnyx/webrtc';
import { NOTIFICATION_TYPE, TelnyxRTC } from '@telnyx/webrtc';

import type {
  SoftphoneCallHandlers,
  SoftphoneClient,
  SoftphoneDialParams,
  SoftphoneMediaStrategy,
  SoftphoneStatus,
} from './softphoneClient.types';
import { VOICE_CALL_ID_HEADER } from './softphoneClient.types';

/** Map Telnyx's verbose call states onto our small status vocabulary. Exported for unit testing. */
export function mapCallState(state: string): SoftphoneStatus | null {
  const s = state.toLowerCase();
  if (s.includes('active')) return 'active';
  if (s.includes('hangup') || s.includes('destroy') || s.includes('purge')) return 'ended';
  // Check setup states before the generic 'ring' match — 'answering' contains the
  // substring 'ring', so it would otherwise be misclassified as 'ringing'.
  if (s.includes('new') || s.includes('request') || s.includes('trying') || s.includes('answering')) {
    return 'connecting';
  }
  if (s.includes('ring') || s.includes('early')) return 'ringing';
  return null;
}

/** Telnyx WebRTC implementation of the provider-agnostic softphone contract. */
export class TelnyxSoftphoneClient implements SoftphoneClient {
  private client: TelnyxRTC | null = null;
  private call: Call | null = null;
  private handlers: SoftphoneCallHandlers | null = null;
  private remoteAudioEl: HTMLAudioElement | null = null;
  // True once we've intentionally ended the call (closeCall/destroy). The carrier still emits a
  // trailing `hangup` callUpdate afterwards; without this guard `handleNotification` would
  // resurrect `this.call` and re-emit `ended`, leaving the controller non-idle after close.
  private closed = false;
  private readonly mediaStrategy?: SoftphoneMediaStrategy;

  constructor(mediaStrategy?: SoftphoneMediaStrategy) {
    this.mediaStrategy = mediaStrategy;
  }

  on(handlers: SoftphoneCallHandlers): void {
    this.handlers = handlers;
  }

  async init(token: string): Promise<void> {
    // Tear down any prior connection so re-dialing doesn't leak a TelnyxRTC instance.
    if (this.client) {
      try {
        await this.client.disconnect();
      } catch {
        // already gone
      }
      this.client = null;
    }
    const client = new TelnyxRTC({ login_token: token });
    client.on('telnyx.error', () => {
      this.handlers?.onError('Voice connection error');
    });
    client.on('telnyx.notification', (notification: INotification) => this.handleNotification(notification));
    this.client = client;
    await client.connect();
  }

  async dial(params: SoftphoneDialParams): Promise<void> {
    const client = this.client;
    if (!client) throw new Error('Softphone is not initialized');

    // Drop any prior call so its state can't surface into this dial (parity with Twilio).
    if (this.call) {
      void this.call.hangup();
      this.call = null;
    }
    // A new call begins — re-open notification handling (a prior closeCall may have closed it).
    this.closed = false;

    const remoteElement = this.ensureRemoteAudioEl();
    const localStream = this.mediaStrategy?.getInputStream ? await this.mediaStrategy.getInputStream() : undefined;

    this.handlers?.onStatus('connecting');
    this.call = client.newCall({
      destinationNumber: params.destinationNumber,
      callerNumber: params.callerNumber ?? undefined,
      remoteElement,
      localStream,
      // When the extension injects a stream we already have audio; otherwise let the SDK capture it.
      audio: localStream ? undefined : true,
      customHeaders: [{ name: VOICE_CALL_ID_HEADER, value: params.voiceCallId }],
      clientState: typeof btoa === 'function' ? btoa(params.voiceCallId) : undefined,
    });
  }

  hangup(): void {
    void this.call?.hangup();
  }

  closeCall(): void {
    // Hang up + clear the call but keep the TelnyxRTC client connected and the remote <audio>
    // element mounted so the next dial reuses them (only `destroy` removes those).
    this.closed = true;
    void this.call?.hangup();
    this.call = null;
  }

  setMuted(muted: boolean): void {
    if (!this.call) return;
    if (muted) this.call.muteAudio();
    else this.call.unmuteAudio();
  }

  sendDigits(digits: string): void {
    // Telnyx names the DTMF method `dtmf` (not `sendDigits`); `this.call` is reassigned on every
    // callUpdate notification, so optional chaining always targets the live call object.
    this.call?.dtmf(digits);
  }

  async destroy(): Promise<void> {
    this.closed = true;
    try {
      await this.call?.hangup();
    } catch {
      // already gone
    }
    this.call = null;
    try {
      await this.client?.disconnect();
    } catch {
      // already disconnected
    }
    this.client = null;
    if (this.remoteAudioEl) {
      this.remoteAudioEl.remove();
      this.remoteAudioEl = null;
    }
  }

  private handleNotification(notification: INotification): void {
    // Ignore trailing notifications after we've intentionally closed (else we'd resurrect the call).
    if (this.closed) return;
    if (notification.type !== NOTIFICATION_TYPE.callUpdate || !notification.call) return;
    this.call = notification.call;
    const status = mapCallState(notification.call.state);
    if (status) this.handlers?.onStatus(status);
  }

  private ensureRemoteAudioEl(): HTMLAudioElement {
    if (this.remoteAudioEl) return this.remoteAudioEl;
    const el = document.createElement('audio');
    el.autoplay = true;
    el.style.display = 'none';
    document.body.appendChild(el);
    this.remoteAudioEl = el;
    return el;
  }
}
